import React, {useState} from 'react';
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmeFrsDictionaryPaper from '@/adjustment/adjust/dialogs/basic/common/SmeFrsDictionaryPaper';
import WokeDoneDictionaryPaper from '@/adjustment/adjust/dialogs/basic/common/WokeDoneDictionaryPaper ';
import type {SmeFrsDictionaryPaperProps} from '@/adjustment/adjust/dialogs/basic/common/SmeFrsDictionaryPaper';
import type {WokeDoneDictionaryPaperProps} from '@/adjustment/adjust/dialogs/basic/common/WokeDoneDictionaryPaper ';

export interface EpWorkingContentProps {
    smeFrsData?: SmeFrsDictionaryPaperProps['data'];
    wokeDoneData?: WokeDoneDictionaryPaperProps['data'];
    /**
     * @deprecated Notes moved to WpNotesControl (table top-left). Kept optional for call-site compat.
     */
    companyId?: number | null;
    /** @deprecated See companyId. */
    annualId?: number | null;
    /** @deprecated See companyId. */
    fsId?: number | null;
    /** @deprecated See companyId. */
    initialNotes?: string | null;
    /** @deprecated See companyId. */
    readOnly?: boolean;
}

/**
 * 合并 SmeFrsDictionaryPaper 与 WokeDoneDictionaryPaper 的手风琴组件。
 * Schedule Notes 已移至各 WP 主表左上角（WpNotesControl / WpNotesAnchor）。
 */
const EpWorkingContent: React.FC<EpWorkingContentProps> = ({
    smeFrsData,
    wokeDoneData,
}) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(prev => ({...prev, [panel]: isExpanded}));
    };

    const hasSmeFrs = smeFrsData && Array.isArray(smeFrsData) && smeFrsData.length > 0
        && smeFrsData.some(item => item.frsParagraphs && item.frsParagraphs.length > 0);
    const hasWokeDone = !!wokeDoneData && (
        (wokeDoneData.wd && wokeDoneData.wd.length > 0) ||
        (wokeDoneData.dynamicWdMaps && Object.keys(wokeDoneData.dynamicWdMaps).length > 0)
    );

    if (!hasSmeFrs && !hasWokeDone) return null;

    return (
        <Box sx={{mt: 2}}>
            {hasSmeFrs && (
                <Accordion
                    expanded={!!expanded['smeFrs']}
                    onChange={handleChange('smeFrs')}
                    disableGutters
                    elevation={0}
                    variant="outlined"
                    sx={{
                        '&:not(:last-child)': {borderBottom: 0},
                        '&::before': {display: 'none'},
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small"/>}
                        sx={{
                            minHeight: 'unset',
                            py: 0.75,
                            px: 2,
                            bgcolor: 'action.hover',
                            '&:hover': {bgcolor: 'action.selected'},
                            '& .MuiAccordionSummary-content': {my: 0},
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <MenuBookIcon fontSize="small" color="primary"/>
                            <Typography variant="subtitle2" color="primary" sx={{fontWeight: 600}}>
                                HKFRS SME-FRS
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{p: 0}}>
                        <SmeFrsDictionaryPaper data={smeFrsData} embed/>
                    </AccordionDetails>
                </Accordion>
            )}

            {hasWokeDone && (
                <Accordion
                    expanded={!!expanded['wokeDone']}
                    onChange={handleChange('wokeDone')}
                    disableGutters
                    elevation={0}
                    variant="outlined"
                    sx={{
                        '&:not(:last-child)': {borderBottom: 0},
                        '&::before': {display: 'none'},
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small"/>}
                        sx={{
                            minHeight: 'unset',
                            py: 0.75,
                            px: 2,
                            bgcolor: 'action.hover',
                            '&:hover': {bgcolor: 'action.selected'},
                            '& .MuiAccordionSummary-content': {my: 0},
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <CheckCircleOutlineIcon fontSize="small" color="primary"/>
                            <Typography variant="subtitle2" color="primary.main" sx={{fontWeight: 600}}>
                                WOKE DONE
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{p: 0}}>
                        <WokeDoneDictionaryPaper data={wokeDoneData} embed/>
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
};

export default EpWorkingContent;
